import { can, PortalPermissions } from "../../../../config/permissions";
import React, { ChangeEvent, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { FilterConfirmProps } from 'antd/lib/table/interface';
import { ColumnType } from 'antd/es/table';
import { Input, Modal, Popconfirm } from 'antd';
import { SearchOutlined } from '@ant-design/icons/lib';
import Select from "antd/lib/select";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import {
  Description, DownloadButton,
  Item,
  ResponsiveColumn,
  ResponsiveRow,
  ResponsiveTable,
  TableContainer,
  TextGroup,
  ButtonContainer,
  Separator
} from "../styles";
import Table, {TableColumn} from "../../../../components/Table";
import {DeleteOutlined, DownloadOutlined, EyeOutlined} from "@ant-design/icons";
import {getAxiosInstanceLinkPagamento} from "../../../../services/providers/api";
import LinkDePagamentoService from "../../service/linkDePagamentoService";
import {TransactionModal} from "../../types";
import {formatCpfOrCnpj, formatPaymentDate, formatStatus} from "../../utils";
import {getErrorMessage} from "../../../../utils/getErrorMessage";
import {message} from "antd";

interface DataType {
  paymentDate: string;
  tempo: string;
  empresa: string;
  document: string;
  businessName: string;
  acquirer: string;
  terminal: string;
  tefTerminal: string;
  brand: string;
  authorizationNumber: string;
  cardNumber: string;
  productName: string;
  acquirerNsu: string;
  originalValue: string;
  status: string;
  parcels: string;
  terminalSerialNumber: string;
  liquidValue: string;
  value: string;
  merchant: string;
}

type DataIndex = keyof DataType;

const LiveTable: React.FC<any> = (props: any) => {
  const {dataFilter, setLink} = props;
  const linkDePagamentoService = new LinkDePagamentoService();
  const user = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo') as string) : null;
  const selectedBusiness = localStorage.getItem('selectedCompany') ? JSON.parse(localStorage.getItem('selectedCompany') as string) :null;
  const [isLoading, setIsLoading] = useState(false);
  const [links, setLinks] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [transactionData, setTransactionData] = useState<TransactionModal | null>(null)

  const filter = (type: string) => {
    const handleAdquirenteFilter = (value: any, record: any) => {
      return record[type].toLowerCase().includes(value.toLowerCase());
    };
    return handleAdquirenteFilter;
  };

  const getLinksDePagamento = async () => {
    try {
      setIsLoading(true);
      const response = await linkDePagamentoService.getAllLinks(selectedBusiness, user);
      setLinks(response);

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);

    } finally {
      setIsLoading(false);
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setIsLoading(true);
      const responseStatus = await linkDePagamentoService.deleteLinkById(id);
      if (responseStatus === 200) await getLinksDePagamento();

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);

    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setLinks(dataFilter);
  }, [dataFilter]);

  const formatMoney = (value:number) => {
    const formattedValue = (value / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formattedValue;
  };

  const handleOpenModal = async (link_id: string) => {
    try {
      setIsLoading(true);
      const response = await linkDePagamentoService.getLinkDePagamentoById(link_id);    
      if (!response?.records || response.records.length === 0) {
        message.warning({
          content: "Nenhum pagamento foi realizado neste link."        
        });
        return;
      }
      setTransactionData(response);
      setModalOpen(true);

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);
      message.error("Erro ao carregar os detalhes do link.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTransactionData(null);
  }

  const downloadPdf = async (transaction: any) => {
    try {
      setIsLoading(true);
      const response = await linkDePagamentoService.getTransactionPdf(transaction);
      if (response) {
        const blob = new Blob([response], { type: "application/pdf" });
        const objectUrl = window.URL.createObjectURL(response);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = "Comprovante de Transação.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(objectUrl);
        document.body.removeChild(a);
      }

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);

    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <LoadingOverlay
        show={isLoading}
        relative
      />
      <TableContainer>
        <Table
          dataSource={dataFilter}
          pagination={false}
        >
          <TableColumn title="Nome" dataIndex="name" key="name" onFilter={filter('name')} />
          <TableColumn title="Descrição" dataIndex="description" key="description" onFilter={filter('description')} />
          <TableColumn title="Valor" render={formatMoney} dataIndex="value" key="value" onFilter={filter('value')} />
          <TableColumn title="URL"
            render={(i: any, e: any) => (<a onClick={() => window.open(e.url, '_blank')}
              style={{
                color: e.status === 'Expirado' ? 'gray' : '#1890ff',
                textDecoration: e.status === 'Expirado' ? 'none' : 'underline',
                cursor: e.status === 'Expirado' ? 'default' : 'pointer',
              }}
            >
              {e.url}
            </a>
            )}
            dataIndex="url"
            key="url"
            onFilter={filter('url')} />
          <TableColumn title="Expira em" dataIndex="expires_at" key="expires_at" onFilter={filter('expires_at')} />
          <TableColumn title="Status" dataIndex="status" key="status" onFilter={filter('status')} />
          <TableColumn
            title="Detalhes do pagamento"
            dataIndex="visualizar"
            key="visualizar"
            render={(_: any, transaction: any) => (dataFilter.length >= 1 ? (
              <EyeOutlined style={{
                fontSize: "20px",
                display: 'flex',
                justifyContent: 'center',
              }} onClick={() => handleOpenModal(transaction.link_id)} />
            ) : null)}
          />

          {can(PortalPermissions.linkPagamento.historico.excluir) && (
            <TableColumn
              title="Excluir"
              dataIndex="excluir"
              key="excluir"
              render={(i: any, e: any) =>
                <Popconfirm
                  title="Deseja realmente excluir?"
                  cancelText="Cancelar"
                  okText="Excluir"
                  onConfirm={() => handleDelete(e.link_id)}
                >
                  <DeleteOutlined style={{
                    fontSize: "16px",
                    display: 'flex',
                    justifyContent: 'center',
                    color: 'red'
                  }}/>
                </Popconfirm>
              }
            />
          )}
        </Table>
      </TableContainer>

      <ResponsiveTable>
        <ResponsiveRow style={{ borderBottom: '1px solid gray', borderRadius: '0px'}}>
          <ResponsiveColumn><h4>Valor</h4></ResponsiveColumn>
          <ResponsiveColumn><h4>Url</h4></ResponsiveColumn>
          <ResponsiveColumn><h4>Expira em</h4></ResponsiveColumn>
        </ResponsiveRow>
        { links.map((links) => {
          return (
            <ResponsiveRow style={{ backgroundColor: 'white',  boxShadow: "2px 2px 6px rgba(0, 0, 0, 0.2)"}}>
              <ResponsiveColumn style={{maxWidth: "92px"}}>
                <p><b>{formatMoney(links.value)}</b></p>
                <p>{links.status}</p>
              </ResponsiveColumn>

              <ResponsiveColumn style={{maxWidth: "200px"}}>
                <a
                  href={links.url}
                  style={{ paddingLeft: "15px", paddingRight: "15px", color: "blue", textDecoration: "underline"}}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.url}
                </a>
              </ResponsiveColumn>

              <ResponsiveColumn>
                <p>{links.expires_at}</p>
              </ResponsiveColumn>
            </ResponsiveRow>
          )
        })}
      </ResponsiveTable>

      <Modal
        title="Detalhes Do Link"
        open={modalOpen}
        onCancel={handleCloseModal}
        footer={null}
        closable={true}
      >
        <h2>Quantidade De Pagamentos: {transactionData?.records?.length}</h2>
        {transactionData?.records.map(transaction => (
          <React.Fragment key={transaction.acquirerNSU}>
            <Separator />
            <TextGroup>
              <Description>Nome Do Portador do Cartão:</Description>
              <Item>{transaction.cardHolder}</Item>
            </TextGroup>
            <TextGroup>
              <Description>Documento:</Description>
              <Item>{transaction.customerNationalId}</Item>
            </TextGroup>
            <TextGroup>
              <Description>Número Do Cartão:</Description>
              <Item>{transaction.cardNumber}</Item>
            </TextGroup>
            <TextGroup>
              <Description>Data Da Transação:</Description>
              <Item>{formatPaymentDate(transaction.paymentDate)}</Item>
            </TextGroup>
            <TextGroup>
              <Description>NSU da Transação:</Description>
              <Item>{transaction.acquirerNSU}</Item>
            </TextGroup>
            <TextGroup>
              <Description>Status Da Transação:</Description>
              <Item>Confirmada</Item>
            </TextGroup>
            <ButtonContainer>
              {can(PortalPermissions.linkPagamento.historico.baixarComprovante) && (
                <DownloadButton onClick={() => downloadPdf(transaction)}>
                  DOWNLOAD DO COMPROVANTE
                  <DownloadOutlined style={{ fontSize: "18px", fontWeight: "500" }} />
                </DownloadButton>
              )}
            </ButtonContainer>
          </React.Fragment>
        ))}
      </Modal>
    </div>
  )
}

export default LiveTable;
